import React, { useState, useEffect, useRef, useCallback } from 'react';
import AustraliaPage from './AustraliaPage';
import UKPage from './UKPage';
import USAPage from './USAPage';
import CanadaPage from './CanadaPage';
import EuropePage from './EuropePage';
import NewZealandPage from './NewZealandPage';

// Import new scroll-based storytelling components
import HeroSection from './components/HeroSection';
import StorySection from './components/StorySection';
import DestinationScroll from './components/DestinationScroll';
import StickyProcessSection from './components/StickyProcessSection';
import CounterSection from './components/CounterSection';
import FinalCTA from './components/FinalCTA';
import ServicesScroll from './components/ServicesScroll';
import WhyChooseSwanScroll from './components/WhyChooseSwanScroll';
import SpecialNZScroll from './components/SpecialNZScroll';
import DocumentChecklistScroll from './components/DocumentChecklistScroll';
import TestimonialsScroll from './components/TestimonialsScroll';
import FAQScroll from './components/FAQScroll';
import ContactScroll from './components/ContactScroll';
import FooterScroll from './components/FooterScroll';

// ==================== ENTRANCE ANIMATION HOOK ====================
const useEntranceAnimation = (options = {}) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    // Add a small delay for smoother entrance
                    setTimeout(() => {
                        setIsVisible(true);
                    }, options.delay || 0);
                }
            },
            { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || '0px' }
        );

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [options.threshold, options.delay, options.rootMargin]);

    return [ref, isVisible];
};

// ==================== PARALLAX HOOK ====================
const useParallax = (speed = 0.5) => {
    const [offset, setOffset] = useState(0);
    const ref = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                // Calculate parallax offset based on element position
                const elementTop = rect.top + scrollTop;
                const viewportHeight = window.innerHeight;

                // Only animate when in viewport
                if (elementTop < scrollTop + viewportHeight && rect.bottom > 0) {
                    const newOffset = (scrollTop - elementTop) * speed;
                    setOffset(newOffset);
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial calculation

        return () => window.removeEventListener('scroll', handleScroll);
    }, [speed]);

    return [ref, offset];
};

// ==================== SCROLL ANIMATION HOOK (Enhanced) ====================
const useScrollAnimation = (options = {}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setIsVisible(true);
                    setHasAnimated(true);
                }
            },
            {
                threshold: options.threshold || 0.1,
                rootMargin: options.rootMargin || '0px'
            }
        );

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [options.threshold, options.rootMargin, hasAnimated]);

    return [ref, isVisible];
};

// ==================== MOUSE PARALLAX HOOK ====================
const useMouseParallax = () => {
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const ref = useRef(null);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (ref.current) {
                const rect = ref.current.getBoundingClientRect();
                const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
                const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
                setPosition({ x, y });
            }
        };

        const element = ref.current;
        if (element) {
            element.addEventListener('mousemove', handleMouseMove);
        }

        return () => {
            if (element) {
                element.removeEventListener('mousemove', handleMouseMove);
            }
        };
    }, []);

    return [ref, position];
};

// ==================== ENTRANCE ANIMATION COMPONENT ====================
// Available for scroll-triggered entrance animations (currently using RevealOnLoad)
// eslint-disable-next-line no-unused-vars
const EntranceAnimation = ({ children, animation = 'fadeInUp', delay = 0, className = '' }) => {
    const [ref, isVisible] = useEntranceAnimation({ delay: delay, threshold: 0.1 });

    const animationClasses = {
        fadeInUp: 'animate-fade-in-up',
        fadeInDown: 'animate-fade-in-down',
        fadeInLeft: 'animate-fade-in-left',
        fadeInRight: 'animate-fade-in-right',
        scaleIn: 'animate-scale-in',
        rotateIn: 'animate-rotate-in',
        bounceIn: 'animate-bounce-in',
    };

    return (
        <div
            ref={ref}
            className={`${isVisible ? animationClasses[animation] || 'animate-fade-in-up' : 'opacity-0'} ${className}`}
        >
            {children}
        </div>
    );
};

// ==================== REVEAL ON LOAD COMPONENT ====================
// Triggers bottom-to-top animation after page loader completes
// eslint-disable-next-line no-unused-vars
const RevealOnLoad = ({ children, delay = 0, className = '' }) => {
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        // Wait for page loader to complete, then trigger animation with delay
        const outerTimer = setTimeout(() => {
            const innerTimer = setTimeout(() => {
                setShouldAnimate(true);
            }, delay);
            return () => clearTimeout(innerTimer);
        }, 800);

        return () => clearTimeout(outerTimer);
    }, [delay]);

    return (
        <div
            ref={ref}
            className={`${className} ${shouldAnimate
                ? 'animate-reveal-bottom'
                : 'opacity-0'
                }`}
            style={{ animationDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

// ==================== PARALLAX COMPONENT ====================
// Available for scroll-based parallax effects
// eslint-disable-next-line no-unused-vars
const ParallaxSection = ({ children, speed = 0.3, className = '' }) => {
    const [ref, offset] = useParallax(speed);

    return (
        <div
            ref={ref}
            className={className}
            style={{ transform: `translateY(${offset}px)` }}
        >
            {children}
        </div>
    );
};

// ==================== MOUSE PARALLAX COMPONENT ====================
// Available for 3D tilt effect on hover
// eslint-disable-next-line no-unused-vars
const MouseParallaxCard = ({ children, className = '' }) => {
    const [ref, position] = useMouseParallax();

    return (
        <div
            ref={ref}
            className={className}
            style={{
                transform: `perspective(1000px) rotateX(${position.y * 5}deg) rotateY(${position.x * 5}deg)`,
                transition: 'transform 0.1s ease-out',
            }}
        >
            {children}
        </div>
    );
};

// ==================== SCROLL ANIMATION COMPONENT (Enhanced) ====================
// eslint-disable-next-line no-unused-vars
const ScrollAnimation = ({ children, direction = 'left', delay = 0, className = '', animationType = 'slide' }) => {
    const [ref, isVisible] = useScrollAnimation({ threshold: 0.15, rootMargin: '0px 0px -50px 0px' });

    const getAnimationClass = () => {
        if (!isVisible) {
            if (direction === 'left') return 'scroll-hidden-left';
            if (direction === 'right') return 'scroll-hidden-right';
            if (direction === 'up') return 'scroll-hidden-up';
            if (direction === 'down') return 'scroll-hidden-down';
            if (animationType === 'scale') return 'scroll-hidden-scale';
            if (animationType === 'rotate') return 'scroll-hidden-rotate';
            return 'scroll-hidden';
        }
        return 'scroll-visible';
    };

    return (
        <div
            ref={ref}
            className={`${getAnimationClass()} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

// ==================== HEADER COMPONENT ====================
const Header = ({ onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = [
        { name: 'Home', href: '#home' },
        { name: 'About', href: '#about' },
        { name: 'Services', href: '#services' },
        { name: 'Countries', href: '#destinations' },
        { name: 'Testimonials', href: '#testimonials' },
        { name: 'FAQ', href: '#faq' },
        { name: 'Contact', href: '#contact' },
    ];

    const handleNavClick = (e, href) => {
        e.preventDefault();
        setMobileMenuOpen(false);
        if (onNavigate) {
            onNavigate(href);
        } else {
            window.location.hash = href;
        }
    };

    return (
        <header
            className={`header fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${scrolled ? 'shadow-lg py-2' : 'shadow-none py-4'
                }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between">
                    {/* Logo - Text only without icon */}
                    <a href="#home" className="flex flex-col" onClick={(e) => handleNavClick(e, '#home')}>
                        <span className="text-2xl font-extrabold text-black">
                            Swan <span className="text-primary-red">Global</span>
                        </span>
                        <span className="text-xs text-gray-500 -mt-1">A Unit of Swan Abroad Education</span>
                    </a>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.href}
                                className="font-medium text-black hover:text-primary-red relative group transition-colors duration-300"
                                onClick={(e) => handleNavClick(e, link.href)}
                            >
                                {link.name}
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-red transition-all duration-300 group-hover:w-full"></span>
                            </a>
                        ))}
                    </nav>

                    {/* CTA Button */}
                    <a
                        href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20book%20a%20free%20consultation%20for%20study%20abroad%20services."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:inline-flex items-center gap-2 bg-primary-red text-white px-6 py-3 rounded-full font-semibold hover:bg-primary-dark transition-all duration-300 hover:shadow-lg hover:shadow-primary-red/30"
                    >
                        <i className="fas fa-calendar-check"></i>
                        Book Free Consultation
                    </a>

                    {/* Mobile Toggle */}
                    <button
                        className="md:hidden text-base font-semibold text-black px-3 py-2 border border-gray-300 rounded-lg"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        aria-label="Toggle menu"
                    >
                        {mobileMenuOpen ? 'Close' : 'Menu'}
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden py-4 border-t animate-fade-in">
                        <nav className="flex flex-col gap-4">
                            {navLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.href}
                                    className="font-medium text-black hover:text-primary-red py-2 transition-colors"
                                    onClick={(e) => handleNavClick(e, link.href)}
                                >
                                    {link.name}
                                </a>
                            ))}
                            <a
                                href="https://wa.me/919000329999?text=Hi%2C%20I%20would%20like%20to%20book%20a%20free%20consultation%20for%20study%20abroad%20services."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-primary-red text-white px-6 py-3 rounded-full font-semibold mt-2"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <i className="fas fa-calendar-check"></i>
                                Book Free Consultation
                            </a>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
};

// ==================== PARTNER CAROUSEL ====================
// eslint-disable-next-line no-unused-vars
const PartnerCarousel = () => {
    const partners = [
        { name: 'University of Melbourne', icon: 'fa-university' },
        { name: 'University of Sydney', icon: 'fa-graduation-cap' },
        { name: 'University of Oxford', icon: 'fa-book-open' },
        { name: 'University of Cambridge', icon: 'fa-certificate' },
        { name: 'Harvard University', icon: 'fa-award' },
        { name: 'MIT', icon: 'fa-microscope' },
        { name: 'Stanford University', icon: 'fa-lightbulb' },
        { name: 'Yale University', icon: 'fa-school' },
        { name: 'University of Toronto', icon: 'fa-building' },
        { name: 'University of British Columbia', icon: 'fa-globe-americas' },
        { name: 'Auckland University', icon: 'fa-map-marked-alt' },
        { name: 'Victoria University', icon: 'fa-user-graduate' },
    ];

    // Create array for potential infinite scroll (currently using regular pagination)
    // const duplicatedPartners = [...partners, ...partners, ...partners];

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const carouselRef = useRef(null);
    const intervalRef = useRef(null);

    const itemsPerSlide = 4;
    const totalSlides = Math.ceil(partners.length / itemsPerSlide);
    const minSwipeDistance = 50;

    const nextSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, [totalSlides]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    }, [totalSlides]);

    const goToSlide = useCallback((index) => {
        setCurrentIndex(index);
    }, []);

    // Auto-rotation with setInterval - 3.5 seconds
    useEffect(() => {
        if (!isPaused) {
            intervalRef.current = setInterval(() => {
                nextSlide();
            }, 3500); // 3.5 seconds for smooth auto-scroll
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPaused, nextSlide]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!carouselRef.current?.contains(document.activeElement)) return;

            if (e.key === 'ArrowLeft') {
                prevSlide();
            } else if (e.key === 'ArrowRight') {
                nextSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [nextSlide, prevSlide]);

    // Touch handlers for swipe
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextSlide();
        } else if (isRightSwipe) {
            prevSlide();
        }
    };

    return (
        <div
            className="partner-carousel"
            ref={carouselRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="region"
            aria-label="Partner Universities Carousel"
            tabIndex={0}
        >
            {/* Section title */}
            <div className="text-center mb-8">
                <h3 className="text-xl md:text-2xl font-bold text-white">
                    <span className="text-primary-red">&bull; </span>
                    Our Partner Universities
                    <span className="text-primary-red"> &bull;</span>
                </h3>
            </div>

            <div className="carousel-container">
                <button
                    className="carousel-btn carousel-btn-prev"
                    onClick={prevSlide}
                    aria-label="Previous slide"
                >
                    <i className="fas fa-chevron-left"></i>
                </button>

                <div className="carousel-track-container">
                    <div
                        className="carousel-track"
                        style={{
                            transform: `translateX(-${currentIndex * 100}%)`,
                            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                            <div key={slideIndex} className="carousel-slide">
                                <div className="partner-logos">
                                    {partners
                                        .slice(slideIndex * itemsPerSlide, slideIndex * itemsPerSlide + itemsPerSlide)
                                        .map((partner, index) => (
                                            <div
                                                key={`${slideIndex}-${index}`}
                                                className="partner-logo-item"
                                                role="img"
                                                aria-label={partner.name}
                                            >
                                                <div className="partner-logo-icon">
                                                    <i className={`fas ${partner.icon}`}></i>
                                                </div>
                                                <span className="partner-logo-name">{partner.name}</span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    className="carousel-btn carousel-btn-next"
                    onClick={nextSlide}
                    aria-label="Next slide"
                >
                    <i className="fas fa-chevron-right"></i>
                </button>
            </div>

            <div className="carousel-dots" role="tablist" aria-label="Slide navigation">
                {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                        key={index}
                        className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                        onClick={() => goToSlide(index)}
                        role="tab"
                        aria-label={`Go to slide ${index + 1}`}
                        aria-selected={index === currentIndex}
                        aria-controls="carousel-track"
                    ></button>
                ))}
            </div>
        </div>
    );
};

// ==================== COOKIE CONSENT ====================
const CookieConsent = () => {
    const [show, setShow] = useState(false);

    useEffect(() => {
        const savedChoice = localStorage.getItem('cookie_consent');
        setShow(!savedChoice);
    }, []);

    const handleChoice = (choice) => {
        localStorage.setItem('cookie_consent', choice);
        setShow(false);
    };

    if (!show) return null;

    return (
        <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-[430px] bg-black/95 text-white p-4 rounded-xl shadow-2xl z-40 border border-white/10">
            <div className="flex flex-col gap-3">
                <p className="text-sm text-gray-300 leading-relaxed">
                    <i className="fas fa-cookie-bite mr-2"></i>
                    We use cookies to improve your experience. By continuing to visit this site you agree to our use of cookies.
                </p>
                <div className="flex gap-2 justify-end">
                    <button
                        onClick={() => handleChoice('accepted')}
                        className="px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                    >
                        Accept
                    </button>
                    <button
                        onClick={() => handleChoice('declined')}
                        className="px-4 py-2 border border-gray-600 rounded-lg hover:bg-gray-800 transition-colors text-sm"
                    >
                        Decline
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==================== SCROLL TO TOP ====================
const ScrollToTop = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const toggleVisibility = () => {
            setVisible(window.scrollY > 500);
        };
        window.addEventListener('scroll', toggleVisibility);
        return () => window.removeEventListener('scroll', toggleVisibility);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (!visible) return null;

    return (
        <button
            onClick={scrollToTop}
            className="fixed bottom-24 right-4 md:right-6 w-12 h-12 bg-primary-red text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-dark transition-all z-40"
            aria-label="Scroll to top"
        >
            <i className="fas fa-arrow-up"></i>
        </button>
    );
};

// ==================== LIVE CHAT WIDGET ====================
const LiveChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { text: "Hello! How can we help you today?", from: "bot" }
    ]);
    const [input, setInput] = useState('');

    const quickReplies = [
        "Visa Process",
        "Scholarships",
        "Universities",
        "Consultation"
    ];

    const sendMessage = (text) => {
        setMessages([...messages, { text, from: "user" }]);
        setTimeout(() => {
            setMessages(prev => [...prev, {
                text: "Thank you for your interest! Our team will contact you shortly. Or call us at +91-9000329999",
                from: "bot"
            }]);
        }, 1000);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (input.trim()) {
            sendMessage(input);
            setInput('');
        }
    };

    return (
        <div className="fixed bottom-4 left-4 md:bottom-6 md:left-6 z-50">
            {isOpen ? (
                <div className="bg-white rounded-xl shadow-2xl w-[calc(100vw-2rem)] max-w-[20rem] h-[70vh] max-h-[30rem] flex flex-col overflow-hidden">
                    <div className="bg-primary-red p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-white font-semibold">Live Chat</span>
                        </div>
                        <button onClick={() => setIsOpen(false)} className="text-white hover:text-gray-200">
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="flex-1 p-4 overflow-y-auto space-y-3">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] px-4 py-2 rounded-lg ${msg.from === 'user' ? 'bg-primary-red text-white' : 'bg-gray-100 text-black'}`}>
                                    {msg.text}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="p-2 border-t flex flex-wrap gap-1">
                        {quickReplies.map((reply, index) => (
                            <button
                                key={index}
                                onClick={() => sendMessage(reply)}
                                className="text-xs px-2 py-1 bg-gray-100 rounded-full hover:bg-gray-200"
                            >
                                {reply}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Type a message..."
                            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary-red"
                        />
                        <button type="submit" className="text-primary-red">
                            <i className="fas fa-paper-plane"></i>
                        </button>
                    </form>
                </div>
            ) : (
                <button
                    onClick={() => setIsOpen(true)}
                    className="w-14 h-14 bg-primary-red text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary-dark transition-all"
                    aria-label="Open chat"
                >
                    <i className="fas fa-comments text-xl"></i>
                </button>
            )}
        </div>
    );
};

// ==================== FLOATING WHATSAPP ====================
const FloatingWhatsApp = () => {
    return (
        <div className="floating-whatsapp">
            <a
                href="https://wa.me/919000329999"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
            >
                <i className="fab fa-whatsapp"></i>
            </a>
        </div>
    );
};

// ==================== MAIN APP ====================
function App() {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [selectedCountry, setSelectedCountry] = useState(null);

    const scrollToSectionById = useCallback((targetId, maxAttempts = 20) => {
        let attempts = 0;
        const yOffset = -100;

        const tryScroll = () => {
            const section = document.getElementById(targetId);

            if (section) {
                const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
                return;
            }

            if (attempts < maxAttempts) {
                attempts += 1;
                window.setTimeout(tryScroll, 100);
            }
        };

        tryScroll();
    }, []);

    const navigateToSection = useCallback((href) => {
        const targetId = href.replace('#', '');
        const scrollToTarget = () => {
            if (targetId === 'home') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                scrollToSectionById(targetId);
            }
            window.history.replaceState(null, '', `#${targetId}`);
        };

        if (selectedCountry) {
            setSelectedCountry(null);
            setTimeout(scrollToTarget, 100);
        } else {
            scrollToTarget();
        }
    }, [scrollToSectionById, selectedCountry]);

    // Handle initial hash navigation on page load
    useEffect(() => {
        const handleInitialHash = () => {
            const hash = window.location.hash.replace('#', '');
            if (hash && hash !== 'home' && hash !== '') {
                setTimeout(() => {
                    scrollToSectionById(hash);
                }, 500);
            }
        };
        // Wait for page to load then check for hash
        setTimeout(handleInitialHash, 100);
        
        // Also handle hash changes
        window.addEventListener('hashchange', handleInitialHash);
        return () => window.removeEventListener('hashchange', handleInitialHash);
    }, [scrollToSectionById]);

    useEffect(() => {
        // Simulate loading progress
        const interval = setInterval(() => {
            setLoadingProgress((prev) => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setTimeout(() => setIsLoaded(true), 300);
                    return 100;
                }
                return prev + Math.random() * 15;
            });
        }, 100);

        return () => clearInterval(interval);
    }, []);

    return (
        <>
            {/* Page Loader */}
            {!isLoaded && (
                <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center">
                    <div className="text-center">
                        {/* Animated Logo */}
                        <div className="relative mb-8">
                            <h1 className="text-5xl font-extrabold animate-pulse">
                                <span className="text-black">Swan</span>
                                <span className="text-primary-red">Global</span>
                            </h1>
                            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                                <div className="w-32 h-1 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-primary-red transition-all duration-300 ease-out"
                                        style={{ width: `${Math.min(loadingProgress, 100)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                        <p className="text-gray-400 mt-6 animate-fade-in">Loading your journey...</p>
                    </div>
                </div>
            )}

            {/* Main Content with bottom-to-top reveal animation */}
            <div className={`font-poppins transition-opacity duration-700 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
                <Header onNavigate={navigateToSection} />
                {selectedCountry === 'australia' ? (
                    <AustraliaPage onBack={() => setSelectedCountry(null)} />
                ) : selectedCountry === 'uk' ? (
                    <UKPage onBack={() => setSelectedCountry(null)} />
                ) : selectedCountry === 'usa' ? (
                    <USAPage onBack={() => setSelectedCountry(null)} />
                ) : selectedCountry === 'canada' ? (
                    <CanadaPage onBack={() => setSelectedCountry(null)} />
                ) : selectedCountry === 'europe' ? (
                    <EuropePage onBack={() => setSelectedCountry(null)} />
                ) : selectedCountry === 'new-zealand' ? (
                    <NewZealandPage onBack={() => setSelectedCountry(null)} />
                ) : (
                    <>
                        {/* New Scroll-Based Storytelling Experience */}
                        <HeroSection />
                        <StorySection />
                        <DestinationScroll onSelectCountry={setSelectedCountry} />
                        <StickyProcessSection />
                        <ServicesScroll />
                        <WhyChooseSwanScroll />
                        <CounterSection />
                        <SpecialNZScroll onSelectCountry={setSelectedCountry} />
                        <DocumentChecklistScroll />
                        <FinalCTA />
                        <ContactScroll />
{/* <SocialProofScroll /> */}
                        <TestimonialsScroll />
                        <FAQScroll />
                        <FooterScroll />
                        <LiveChat />
                        <ScrollToTop />
                        <CookieConsent />
                    </>
                )}
                <FloatingWhatsApp />
            </div>
        </>
    );
}

export default App;
